/* eslint-disable array-callback-return */

import { ChangeEvent, useEffect, useMemo, useState } from 'react';

import { useAtom } from 'jotai';
import { useRouter } from 'next/router';
import { HiFilter, HiLocationMarker } from 'react-icons/hi';
import { IoFilterOutline } from 'react-icons/io5';
import { useWindowSize } from 'usehooks-ts';

import { categories, delegations, governorates } from '@/lib/helpers';
import { pageLoading } from '@/lib/store';
import CheckBox from '@/tayara-kit/Checkbox';
import CriteriaSelect from '@/tayara-kit/CriteriaSelect';
import MultiSelectCheckbox from '@/tayara-kit/MultiSelectCheckbox';
import Range from '@/tayara-kit/Range';

import DualColumn from './DualColumn';
import FilterBadge from './FilterBadge';
import NarrowFilterScaffold from './NarrowFilterScaffold';
import { useElastisearchFilter } from '../hooks/useElasticSearchServer';
import useFilterSelect from '../hooks/useFilterSelect';

interface AdvancedSearchBarProps {
  className?: string;
  hideOnScrollDown?: boolean;
}

const AdvancedSearchBar: React.FC<AdvancedSearchBarProps> = ({
  className,
  hideOnScrollDown = false,
}) => {
  const {
    selectedPrice,
    handlePriceRange,
    governorate,
    handleGovernorate,
    delegation,
    handleDelegation,
    delegationList,
    category,
    handleCategory,
    subCategory,
    handleSubCategory,
    subCategoryList,
    optionsParams,
    handleOptionsParams,
    rangeParams,
    handleRangeParams,
    paramsList,
    handleResetFilter,
    handleProductType,
    productType,
    handleOptionsParamsInput,
  } = useFilterSelect(() => ({}));

  const [initSearchWithFilter] = useElastisearchFilter();
  const router = useRouter();
  const [isImmoNeuf, setIsImmoNeuf] = useState(false);
  const [showSearchBar, setShowSearchBar] = useState(true);
  const [prevScrollPos, setPrevScrollPos] = useState(0);

  useEffect(() => {
    const isImmo =
      router.pathname
        .split('/')
        .some((segment) => segment.toLowerCase() === 'immoneuf') ||
      router?.query?.category === 'ImmoNeuf';
    setIsImmoNeuf(isImmo);
  }, [router]);

  useEffect(() => {
    if (!hideOnScrollDown) {
      return undefined;
    }

    const handleScroll = () => {
      const currentScrollPos = window.pageYOffset;
      const scrollingDown = currentScrollPos > prevScrollPos;
      const scrollingUp = currentScrollPos < prevScrollPos;

      // Show when scrolling up, hide when scrolling down
      // Also show if at the top of the page
      if (currentScrollPos < 50) {
        setShowSearchBar(true);
      } else if (scrollingUp) {
        setShowSearchBar(true);
      } else if (scrollingDown) {
        setShowSearchBar(false);
      }

      setPrevScrollPos(currentScrollPos);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hideOnScrollDown, prevScrollPos]);

  const displayListParams = useMemo(
    () =>
      paramsList.flatMap(
        ({ name = '', type, possiblesValues, disabled }, i) => {
          switch (type) {
            case 'array':
              return [
                <div className="pt-4" key={i}>
                  <CriteriaSelect
                    onChange={(event) => {
                      handleOptionsParams(event, name);
                    }}
                    value={
                      optionsParams !== undefined &&
                      Object.keys(optionsParams).length !== 0
                        ? optionsParams[name]
                        : undefined
                    }
                    displayLabel={name || ''}
                    disabled={disabled}
                    options={
                      possiblesValues?.map((possiblesValue) => ({
                        label: possiblesValue,
                        value: possiblesValue,
                        display: possiblesValue,
                      })) ?? []
                    }
                  ></CriteriaSelect>
                </div>,
              ];
            case 'checkbox':
              return possiblesValues
                ? possiblesValues.map((value, index) => (
                    <div className="w-full" key={`checkbox-${index}`}>
                      <label className="text-sm text-gray-700 font-bold m-2">
                        <input
                          checked={optionsParams && value in optionsParams}
                          className="w-4 h-4 accent-neuf-200 text-neuf-200 bg-gray-100 rounded border-gray-300    dark:bg-gray-700 dark:border-gray-600 mx-1 "
                          type="checkbox"
                          value={value}
                          onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            handleOptionsParamsInput(event, value);
                          }}
                        />
                        {value}
                      </label>
                    </div>
                  ))
                : [];

            case 'number':
              return [
                <div className="col-span-2" key={i}>
                  <Range
                    valueMax={rangeParams[name]?.selectedMax || ''}
                    valueMin={rangeParams[name]?.selectedMin || ''}
                    onChangeMax={(selectedMax) =>
                      handleRangeParams('setMax', name, selectedMax)
                    }
                    onChangeMin={(selectedMin) =>
                      handleRangeParams('setMin', name, selectedMin)
                    }
                    displayLabel={name || ''}
                  />
                </div>,
              ];

            default:
              return [];
          }
        }
      ),
    [
      paramsList,
      optionsParams,
      handleOptionsParams,
      rangeParams,
      handleRangeParams,
      handleOptionsParamsInput,
    ]
  );

  const windowSize = useWindowSize();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading] = useAtom(pageLoading);

  return (
    <>
      {windowSize.width < 1024 ? (
        <>
          {!isLoading && (
            <div
              className={`fixed top-[150px] left-0 w-full transition-all duration-300 z-[9998] ${
                className || ''
              } ${
                hideOnScrollDown && !showSearchBar
                  ? '-translate-y-[200px] opacity-0'
                  : 'translate-y-0 opacity-100'
              }`}
            >
              <div className="w-full py-2 px-4 flex gap-1 justify-center align-middle items-center bg-white/90 bg-blend-overlay backdrop-blur-xl">
                <span
                  className={`flex justify-center w-full px-4 py-2.5 rounded-lg ${
                    isImmoNeuf ? 'bg-neuf-200' : 'bg-primary'
                  } items-center font-light text-gray-700 capitalize space-x-2 cursor-pointer`}
                  onClick={() => setIsOpen(true)}
                >
                  <IoFilterOutline className="text-white" size={18} />
                  <span className="text-white truncate text-sm font-semibold">
                    Filtres Avancés
                  </span>
                </span>
              </div>
            </div>
          )}

          {isOpen && (
            <div className="fixed overflow-hidden inset-0 flex justify-start flex-col h-full bg-white z-[9999]">
              <NarrowFilterScaffold
                onResetFilter={handleResetFilter}
                setIsOpen={setIsOpen}
                isOpen={isOpen}
              >
                <div className="w-full lg:h-[calc(100vh-260px)] lg:overflow-y-auto">
                  <FilterBadge />
                  <DualColumn>
                    <div className="col-span-2">
                      <Range
                        displayLabel="Prix (DT)"
                        onChangeMax={(value) =>
                          handlePriceRange({
                            min: selectedPrice.min,
                            max: value,
                          })
                        }
                        onChangeMin={(value) =>
                          handlePriceRange({
                            min: value,
                            max: selectedPrice.max,
                          })
                        }
                        valueMin={selectedPrice.min || ''}
                        valueMax={selectedPrice.max || ''}
                      />
                    </div>
                    <CriteriaSelect
                      icon={HiLocationMarker}
                      onChange={handleGovernorate}
                      displayLabel="Ville"
                      value={governorate !== null ? governorate.id : undefined}
                      options={governorates
                        .map(({ id, name, dbvalue }) => ({
                          value: id,
                          label: name,
                          display: dbvalue || name,
                        }))
                        .sort((a, b) => {
                          if (a.label < b.label) {
                            return -1;
                          }
                          if (a.label > b.label) {
                            return 1;
                          }
                          return 0;
                        })}
                    ></CriteriaSelect>
                    <MultiSelectCheckbox
                      values={delegation}
                      onChange={handleDelegation}
                      displayLabel="Délégation"
                      options={delegationList
                        .map(({ id, name }) => ({
                          value: id,
                          label: name,
                          display:
                            delegations.find((d) => d.id === id)?.dbvalue ||
                            name,
                        }))
                        .sort((a, b) => {
                          if (a.label > b.label) {
                            return -1;
                          }
                          if (a.label < b.label) {
                            return 1;
                          }
                          return 0;
                        })}
                    ></MultiSelectCheckbox>
                    <CriteriaSelect
                      onChange={handleCategory}
                      value={category.id}
                      displayLabel="Catégorie"
                      options={categories
                        .filter((cat) => cat.parentcategoryxid === '')
                        .map(({ id, name, dbvalue }) => ({
                          value: id,
                          label: name,
                          display: dbvalue || name,
                        }))}
                    ></CriteriaSelect>
                    <CriteriaSelect
                      onChange={handleSubCategory}
                      value={subCategory.id}
                      displayLabel="Sous-catégorie"
                      options={subCategoryList.map(
                        ({ value, label, display }) => ({
                          value,
                          label,
                          display: display || '',
                        })
                      )}
                    ></CriteriaSelect>
                    {!isImmoNeuf ? (
                      <CheckBox
                        onChange={handleProductType}
                        options={[
                          {
                            label: 'Neuf',
                            id: 1,
                          },
                          {
                            label: 'Occasion',
                            id: 0,
                          },
                        ]}
                        displayLabel={'État de produit :'}
                        value={productType}
                      />
                    ) : (
                      <></>
                    )}
                  </DualColumn>
                  <DualColumn>{displayListParams}</DualColumn>
                </div>
                <div className="relative flex w-full gap-x-2 h-[3rem] mt-4 lg:mt-0">
                  <button
                    aria-label="Filtrer"
                    className={`flex w-full items-center justify-center ${
                      isImmoNeuf
                        ? 'bg-neuf-200'
                        : 'bg-primary hover:bg-primary-focus'
                    } rounded-lg w-5/6`}
                    onClick={() => {
                      initSearchWithFilter();
                      setIsOpen(false);
                    }}
                  >
                    <HiFilter size={20} className="text-white mr-6" />
                    <span className="tracking-widest uppercase font-normal text-white">
                      Filtrer
                    </span>
                  </button>
                </div>
              </NarrowFilterScaffold>
            </div>
          )}
        </>
      ) : null}
    </>
  );
};

export default AdvancedSearchBar;
